import React, { useCallback, useEffect } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import { useParams, useHistory } from 'react-router';
import { useIntl } from 'react-intl';
import { Tab, Tabs } from '@tovia/man-ui';
import { ContentHeader } from 'src/client/components/ui/ContentHeader';
import { Link } from 'react-router-dom';
import LoaderWrapper from '../../components/Loader/LoaderWrapper';
import GalleriesContent from '../Galleries/GalleriesContent/GalleriesContent';
import { load } from '../../redux/modules/photographer';
import { parseSlug } from '../../utils/converters';
import PhotographerPanels from '../Panels/PhotographerPanels';
import { ContentListSection } from '../ContentListSection/ContentListSection';
import { useSelector } from '../../redux/modules/helpers/useSelector';
import Helmet from '../Helmet/Helmet';
import urls from 'src/shared/urls';
import { isPrerender } from '../../utils/prerenderDetect';

export default function Photographer() {
  const config = useSelector((state) => state.app.config);
  const pixelApiUrl = useSelector((state) => state.app.pixelApiUrl);
  const { loading, loaded, item, error } = useSelector((state) => state.photographer);
  const site = useSelector((state) => state.site);

  const params = useParams<{ photographerName: string; tab: string }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const intl = useIntl();

  useEffect(() => {
    if (error?.status === 404) {
      history.replace(urls.get.notFound);
    }
  }, [error, history]);

  const { photographerPageTabs } = config;

  const loadData = useCallback(
    (photographerName) => {
      const loadParams = {
        name: parseSlug(photographerName),
        first: 20,
      };
      dispatch(load(loadParams));
    },
    [dispatch],
  );

  useEffect(() => {
    loadData(params.photographerName);
  }, [loadData, params.photographerName]);

  useEffect(() => {
    if (!isPrerender && item) {
      window.dataLayer.push({
        event: 'select_content',
        content_type: 'photographer',
        item_id: item.name,
      });
    }
  }, [item]);

  const renderContentHeader = (photographerName, activeTabValue) => {
    const { moviesCount, galleriesCount } = item;

    const tabs = [
      {
        value: 'latest',
        to: `/photographer/${photographerName}/latest`,
        name: intl.formatMessage({ id: 'photographer.tabs.latest', defaultMessage: 'Latest' }),
      },
      ...(moviesCount > 0
        ? [
            {
              value: 'movies',
              to: `/photographer/${photographerName}/movies/top`,
              name: intl.formatMessage({ id: 'photographer.tabs.movies', defaultMessage: 'Movies' }),
            },
          ]
        : []),
      ...(galleriesCount > 0
        ? [
            {
              value: 'photos',
              to: `/photographer/${photographerName}/photos/top`,
              name: intl.formatMessage({ id: 'photographer.tabs.photos', defaultMessage: 'Photos' }),
            },
          ]
        : []),
    ].filter((tab) => photographerPageTabs.includes(tab.value)); // allows tabs to be configurable from db

    const activeTab = tabs.find((tab) => tab.value === activeTabValue);

    if (!activeTab) {
      return null;
    }

    const tabsNode = (
      <Tabs>
        {tabs.map((tab) => (
          <Tab key={tab.value} as={Link} active={activeTab.value === tab.value} {...tab} />
        ))}
      </Tabs>
    );
    return <ContentHeader tabs={tabsNode} />;
  };

  const renderContent = useCallback(
    (_item, tab) => {
      if (!tab) {
        return null;
      }

      const { movies, galleries, name, UUID } = _item;

      const isLatest = tab === 'latest';

      const contents = [
        ...(movies.length
          ? [
              {
                title: intl.formatMessage(
                  { id: 'photographer.headline.latestMovies', defaultMessage: `Latest movies by ${name}` },
                  { photographerName: name },
                ),
                type: 'movies',
              },
            ]
          : []),
        ...(galleries.length
          ? [
              {
                title: intl.formatMessage(
                  { id: 'photographer.headline.latestGalleries', defaultMessage: `Latest galleries by ${name}` },
                  { photographerName: name },
                ),
                type: 'galleries',
              },
            ]
          : []),
      ];

      if (isLatest) {
        return contents.map((content) => (
          <ContentListSection key={content.title} title={content.title} items={_item[content.type]} />
        ));
      }
      return (
        <GalleriesContent UUID={UUID} galleryType={tab === 'movies' ? 'MOVIE' : 'GALLERY'} pageType="photographer" />
      );
    },
    [intl],
  );

  const activeTab = params.tab || 'latest';

  const loaderMarkup =
    !item || loading ? (
      <LoaderWrapper
        defaultHeight
        reloadFunction={() => loadData(params.photographerName)}
        loadingStatus={loading}
        reduxErrorStatus={error}
        contentType="model"
      />
    ) : null;

  const helmetMarkup = item ? (
    <Helmet
      id="photographer"
      canonicalUrl={`${window.location.origin}/photographer/${params.photographerName}`}
      defaultTitle="{{photographerName}} - {{domainName}}"
      variables={{
        photographerName: item.name,
        setsCount: item.galleriesCount,
        debutYear: moment(item.firstPublishAt).format('YYYY'),
        domainName: site.domain,
        siteName: site.name,
      }}
    />
  ) : null;

  const trackingPixel = item ? (
    <img src={`${pixelApiUrl}/${site.UUID}/PHOTOGRAPHER/${item.UUID}`} style={{ display: 'none' }} alt="pixel" />
  ) : null;
  const photographerDetailsMarkup = item || loaded ? <StyledPhotographerPanels photographer={item} /> : null;
  const tabsHeaderMarkup =
    (item || loaded) && activeTab ? renderContentHeader(params.photographerName, activeTab) : null;
  const photographerContentMarkup = item && !loading ? renderContent(item, activeTab) : null;

  return (
    <div className="content container">
      {helmetMarkup}
      {trackingPixel}
      {photographerDetailsMarkup}
      {tabsHeaderMarkup}
      {loaderMarkup}
      {photographerContentMarkup}
    </div>
  );
}

const StyledPhotographerPanels = styled(PhotographerPanels)`
  margin-top: 20px;
`;
